import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, CirclePlayIcon, ChevronRightIcon } from 'lucide-react';
import type { Tool } from '../../types/tools';
import { GET_STARTED_URL } from '../../data/dgpt';
import { CtaButton } from '../ui/CtaButton';
import { Eyebrow } from '../ui/Eyebrow';
import { Reveal } from '../ui/Reveal';
import { ToolOutputPreview } from './ToolOutputPreview';
type ToolHeroProps = {
  tool: Tool;
  showIllustrativeCounts: boolean;
};
export function ToolHero({
  tool,
  showIllustrativeCounts
}: ToolHeroProps) {
  return <section aria-labelledby="tool-heading" className="relative overflow-hidden border-b border-hairline-soft bg-ink" data-id="element-1522" data-mp="17xknas3v7x83">
      <div className="sf-grid-texture sf-fade-mask pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-1523" data-mp="1gw668od91e9e" />
      <div className="relative mx-auto grid w-full max-w-[1320px] gap-12 px-4 pb-16 pt-8 sm:px-6 sm:pt-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:items-center lg:gap-14 lg:px-8 lg:pb-24 lg:pt-12" data-id="element-1524" data-mp="3nlukln1lnun5">
        <div className="flex min-w-0 flex-col items-start" data-id="element-1525" data-mp="2f35zvqup8jo5">
          <nav aria-label="Breadcrumb" data-id="element-1526" data-mp="2f0jtvvngic81">
            <ol className="flex flex-wrap items-center gap-1 text-xs text-text-tertiary" data-id="element-1527" data-mp="2k2ivp13jt12v">
              <li data-id="element-1528" data-mp="2p0alnpkmud4f">
                <Link to="/" className="rounded hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-1529" data-mp="2ozshm6rqauly">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1530" data-mp="2xscildvhtb9y">
                <ChevronRightIcon className="h-3 w-3" data-id="element-1531" data-mp="32ryvtnbbmrir" />
              </li>
              <li data-id="element-1532" data-mp="32q4eeiz9dhjf">
                <Link to="/tools" className="rounded hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-1533" data-mp="37r23zgmllduy">
                  Tools
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-1534" data-mp="1ddalrsq7dlnw">
                <ChevronRightIcon className="h-3 w-3" data-id="element-1535" data-mp="1dec6yq0xnojc" />
              </li>
              <li aria-current="page" className="text-text-secondary" data-id="element-1536" data-mp="1ibc4gcjpviyu">
                {tool.name}
              </li>
            </ol>
          </nav>

          <Eyebrow className="mt-8" data-id="element-1537" data-mp="1adijrvqw9nti">DiligenceGPT™ Tools</Eyebrow>
          <h1 id="tool-heading" className="mt-4 text-balance text-[40px] font-semibold leading-[1.02] tracking-[-0.04em] text-text-primary sm:text-[56px] lg:text-[64px]" data-id="element-1538" data-mp="15deaoqsbfvdb">
            {tool.name}
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-text-secondary sm:text-lg" data-id="element-1539" data-mp="1fcvfl5zqu588">{tool.hero.supporting}</p>

          <ul className="mt-6 flex flex-col gap-2.5" data-id="element-1540" data-mp="1obgyx3d1hoa2">
            {tool.hero.bullets.map(bullet => <li key={bullet} className="flex items-start gap-2.5 text-[15px] text-text-primary" data-id="element-1541" data-mp="230msaap74ka1">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F5F3FC] text-lime" data-id="element-1542" data-mp="1y0gzgr76wrqd">
                  <CheckIcon className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" data-id="element-1543" data-mp="2c1l0l56o3dyf" />
                </span>
                {bullet}
              </li>)}
          </ul>

          <div className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center" data-id="element-1544" data-mp="2qojoiq6bfq8e">
            <CtaButton href={GET_STARTED_URL} event="tool_cta_click" eventParams={{
            tool: tool.slug,
            placement: 'hero'
          }} size="xl" className="w-full sm:w-auto" data-id="element-1545" data-mp="2vlih34abzp4h">
              Try {tool.name}
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1546" data-mp="27x8z6dg8nlim" />
            </CtaButton>
            <a href="#demo" className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold text-text-primary transition-colors hover:text-lime focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-1547" data-mp="2cnfmm9z0kzza">
              <CirclePlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-1548" data-mp="1oyemrb48yx5m" />
              Watch Demo
            </a>
          </div>

          <UsageProof tool={tool} showIllustrativeCounts={showIllustrativeCounts} data-id="element-1549" data-mp="1tw6cpzlc0976" />
        </div>

        <Reveal delay={0.08} className="min-w-0" data-id="element-1550" data-mp="2w9kpqnx72hk5">
          <ToolOutputPreview tool={tool} variant="compact" data-id="element-1551" data-mp="2wcglq6phew5y" />
        </Reveal>
      </div>
    </section>;
}

/**
 * Social-proof slot. Shows a verified count when configured; an illustrative
 * count only in design preview (clearly labelled); otherwise a neutral fact line.
 */
function UsageProof({
  tool,
  showIllustrativeCounts
}: ToolHeroProps) {
  const format = (value: number) => value.toLocaleString('en-US');
  if (tool.verifiedUsageCount !== null) {
    return <p className="mt-6 text-sm text-text-secondary" data-id="element-1552" data-mp="28ji2cl485zrv">
        <span className="font-semibold text-text-primary" data-id="element-1553" data-mp="24iy39c03cm33">{format(tool.verifiedUsageCount)}</span> professionals have
        tried this tool.
      </p>;
  }
  if (showIllustrativeCounts) {
    return <p className="mt-6 flex flex-wrap items-center gap-2 text-sm text-text-secondary" data-id="element-1554" data-mp="2scxg7qxiu22">
        <span data-id="element-1555" data-mp="3txuu39kgdhef">
          <span className="font-semibold text-text-primary" data-id="element-1556" data-mp="3tzfmasjaoo94">{format(tool.illustrativeUsageCount)}</span> professionals
          have tried this tool.
        </span>
        <span className="rounded-md border border-[#F0E0C4] bg-[#FDF7ED] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[#92400E]" data-id="element-1557" data-mp="abm4hubyqgny">
          Illustrative count
        </span>
      </p>;
  }
  return <p className="mt-6 text-xs text-text-tertiary" data-id="element-1558" data-mp="k1ozg02na4ox">
      Uses your fund’s criteria · Outputs designed for analyst review
    </p>;
}